import { useMemo, useState } from 'react'
import {
  Box, InputAdornment, Popover, Stack, TextField, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import type { LocationTreeNode } from '@cmms/types'
import { primary } from '../theme/palette'
import { api } from '../lib/api'
// HSE fork: the locations feature module was stripped from the UI,
// so the category-view hook that gave each location type a coloured
// icon and label lives nowhere anymore. TreeLocationPicker still
// works — it just falls back to a plain folder icon regardless of
// node type. If the categorised view is ever needed here, port the
// old `useCategoryView` from the CMMS repo back into this file.
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
const useCategoryView = (_type: string) => ({
  Icon: FolderOutlinedIcon,
  // Neutral grey defaults so the icon chip renders regardless of node
  // type. Original CMMS shim returned per-type colours from the
  // categories module.
  color: { bg: '#F3F4F6', bgSel: '#E5E7EB', fg: '#4B5563' },
})

interface Props {
  siteId: string
  value: string  // selected location ID
  onChange: (locationId: string, locationName: string) => void
  onClear: () => void
  /** Display name of the currently selected location */
  displayName?: string
}

export function TreeLocationPicker({ siteId, value, onChange, onClear, displayName }: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [search, setSearch] = useState('')
  const open = Boolean(anchorEl)

  const treeQ = useQuery({
    queryKey: ['location-tree', siteId],
    queryFn: () => api.locations.tree(siteId),
    enabled: Boolean(siteId) && open,
    staleTime: 30_000,
  })
  const tree: LocationTreeNode[] = Array.isArray(treeQ.data) ? treeQ.data : []

  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Auto-expand ancestors of the selected value
  useMemo(() => {
    if (!value || tree.length === 0) return
    const ancestors = findAncestors(tree, value)
    setExpanded((prev) => {
      const next = new Set(prev)
      ancestors.forEach((id) => next.add(id))
      // Also expand root nodes
      tree.forEach((n) => next.add(n.id))
      return next
    })
  }, [tree, value])

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Search: find matching nodes + auto-expand their ancestors
  const matchIds = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return null
    const hits = new Set<string>()
    function walk(nodes: LocationTreeNode[]) {
      for (const n of nodes ?? []) {
        if (n.name.toLowerCase().includes(q)) hits.add(n.id)
        walk(n.children ?? [])
      }
    }
    walk(tree)
    const visible = new Set<string>()
    function mark(nodes: LocationTreeNode[], ancestors: string[]): boolean {
      let any = false
      for (const n of nodes ?? []) {
        const childHit = mark(n.children ?? [], [...ancestors, n.id])
        if (hits.has(n.id) || childHit) {
          visible.add(n.id)
          ancestors.forEach((a) => visible.add(a))
          any = true
        }
      }
      return any
    }
    mark(tree, [])
    return { hits, visible }
  }, [search, tree])

  function handleSelect(node: LocationTreeNode) {
    onChange(node.id, node.name)
    setSearch('')
    setAnchorEl(null)
  }

  return (
    <>
      {/* Trigger button */}
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'flex', alignItems: 'center',
          px: 1.75, py: 1,
          borderRadius: 1,
          border: '1px solid', borderColor: open ? 'primary.main' : 'divider',
          cursor: 'pointer', minHeight: 40,
          transition: 'border-color 120ms ease',
          '&:hover': { borderColor: 'text.secondary' },
        }}
      >
        <LocationOnOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
        <Typography sx={{ flex: 1, fontSize: 13.5, color: value ? 'text.primary' : 'text.secondary' }} noWrap>
          {displayName || 'Select location'}
        </Typography>
        {value && (
          <Box component="span" onClick={(e) => { e.stopPropagation(); onClear() }}
            sx={{ ml: 0.5, display: 'inline-flex', cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
      </Box>

      {/* Tree popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: anchorEl?.offsetWidth ?? 300, maxHeight: 380, borderRadius: 1.5, mt: 0.5, overflow: 'hidden', display: 'flex', flexDirection: 'column' } } }}
      >
        {/* Search */}
        <Box sx={{ px: 1.25, pt: 1.25, pb: 0.75, flexShrink: 0 }}>
          <TextField
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search locations…" size="small" fullWidth autoFocus
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 1.25, '& fieldset': { borderColor: 'divider' } } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: 'text.secondary' }} /></InputAdornment> }}
          />
        </Box>

        <Box sx={{
          flex: 1, overflowY: 'auto', py: 0.75,
          scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
          '&:hover::-webkit-scrollbar-thumb': {
            background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)',
          },
        }}>
          {treeQ.isLoading && (
            <Typography sx={{ px: 2, py: 2, fontSize: 12.5, color: 'text.secondary' }}>Loading locations…</Typography>
          )}
          {!treeQ.isLoading && tree.length === 0 && (
            <Typography sx={{ px: 2, py: 2, fontSize: 12.5, color: 'text.secondary' }}>No locations in this site.</Typography>
          )}
          {matchIds && matchIds.hits.size === 0 && (
            <Typography sx={{ px: 2, py: 2, fontSize: 12.5, color: 'text.secondary' }}>No locations match "{search}"</Typography>
          )}
          {tree.map((node) => (
            <TreeNode key={node.id} node={node} depth={0} selectedId={value}
              expanded={expanded} onToggle={toggle} onSelect={handleSelect} matchIds={matchIds} />
          ))}
        </Box>
      </Popover>
    </>
  )
}

// ─── Tree node ──────────────────────────────────────────────────────────────

function TreeNode({ node, depth, selectedId, expanded, onToggle, onSelect, matchIds }: {
  node: LocationTreeNode; depth: number; selectedId: string
  expanded: Set<string>; onToggle: (id: string) => void
  onSelect: (node: LocationTreeNode) => void
  matchIds: { hits: Set<string>; visible: Set<string> } | null
}) {
  if (matchIds && !matchIds.visible.has(node.id)) return null
  const children = node.children ?? []
  const hasChildren = children.length > 0
  const isExpanded = expanded.has(node.id) || Boolean(matchIds)
  const isSelected = node.id === selectedId
  const cat = useCategoryView(node.type)
  const Icon = cat.Icon

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}
        onClick={() => onSelect(node)}
        sx={{
          pl: 1.25 + depth * 2.25, pr: 1.5, py: 0.85,
          cursor: 'pointer',
          bgcolor: isSelected ? primary.selected : 'transparent',
          transition: 'background-color 100ms ease',
          '&:hover': { bgcolor: isSelected ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)' },
        }}
      >
        <Box
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.id) }}
          sx={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: 'text.secondary', flexShrink: 0, opacity: hasChildren ? 1 : 0, cursor: hasChildren ? 'pointer' : 'default' }}>
          <motion.div animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.15 }} style={{ display: 'inline-flex' }}>
            <ChevronRightIcon sx={{ fontSize: 15 }} />
          </motion.div>
        </Box>

        <Box sx={{
          width: 26, height: 26, borderRadius: 1,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: isSelected ? cat.color.bgSel : cat.color.bg,
          color: cat.color.fg, flexShrink: 0,
        }}>
          <Icon sx={{ fontSize: 14 }} />
        </Box>

        <Typography sx={{ flex: 1, fontSize: 13, fontWeight: isSelected ? 600 : 500, letterSpacing: '-0.005em' }} noWrap>
          {node.name}
        </Typography>
      </Stack>

      {hasChildren && isExpanded && children.map((child) => (
        <TreeNode key={child.id} node={child} depth={depth + 1} selectedId={selectedId}
          expanded={expanded} onToggle={onToggle} onSelect={onSelect} matchIds={matchIds} />
      ))}
    </Box>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function findAncestors(nodes: LocationTreeNode[], targetId: string, path: string[] = []): string[] {
  for (const n of nodes ?? []) {
    if (n.id === targetId) return [...path, n.id]
    const children = n.children ?? []
    if (children.length > 0) {
      const found = findAncestors(children, targetId, [...path, n.id])
      if (found.length > 0) return found
    }
  }
  return []
}
